import type { Campaign } from '~/types/graphql';

import {
  determineTemplateStatus,
  ETemplateStatus,
  useUnpublishedThemePages,
} from '~/modules/campaign-template';
import { getThemePageIDBySuffix, useModalPublishCampaignStore } from '~/modules/campaign';
import { useThemePageStore } from '~/stores/themePage';
import { useShopifyAsset } from '~/modules/shopify';
import { useAssetsStore } from '~/stores/assets';
import { useGPVariantB } from './useGPVariantB';
import { useMemo } from 'react';

export const useUnPublishTemplateB = ({ campaigns }: { campaigns: Campaign[] }) => {
  const assets = useAssetsStore((state) => state.assets);
  const publishedThemePageIds = useModalPublishCampaignStore(
    (state) => state.publishedThemePageIds,
  );

  const { variants, themePageIdsB } = useGPVariantB({ campaigns });
  const { isFetching: isShopifyAssetFetching } = useShopifyAsset();
  const allThemePagesMap = useThemePageStore((state) => state.allThemePagesMap);
  const { isFetchingUnpublishedThemePages } = useUnpublishedThemePages();

  const allAssets = useMemo(() => {
    if (!assets) return [];

    return [...assets.templateGPV6, ...assets.templateGPV7, ...assets.templateShopify];
  }, [assets]);

  const unPublishedTemplateIdsB = useMemo(() => {
    const results: string[] = [];

    if (isShopifyAssetFetching || isFetchingUnpublishedThemePages) return results;

    variants.forEach((variant) => {
      const { templateSuffix } = variant || {};
      const themePageId = getThemePageIDBySuffix(templateSuffix);

      if (!themePageId || !templateSuffix || themePageId === '0') return;

      const isInPublished = publishedThemePageIds.includes(themePageId);
      if (isInPublished) return;

      const { status } = determineTemplateStatus({
        templateSuffix,
        themePageId,
        assets: allAssets,
        themePages: allThemePagesMap,
      });

      if (status !== ETemplateStatus.UNPUBLISHED) return;

      results.push(themePageId);
    });

    return results;
  }, [
    variants,
    publishedThemePageIds,
    isShopifyAssetFetching,
    isFetchingUnpublishedThemePages,
    allAssets,
    allThemePagesMap,
  ]);

  return { unPublishedTemplateIdsB, themePageIdsB };
};
